import React, { useState } from 'react';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../lib/api';
import { fehlertext } from '../../lib/fehlertext';
import { useAuth } from '../../contexts/AuthContext';
import { codeGueltig } from '../../lib/zweiFaktor';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import { Assistent } from './Assistent';
import { CodesAnzeige } from './CodesAnzeige';

const ALLGEMEIN = 'Das hat nicht geklappt. Bitte versuchen Sie es erneut.';
const EINGERICHTET = 'Die Zwei-Faktor-Anmeldung ist eingerichtet.';

// Solange Codes sichtbar sind, schließt der Dialog nur über „Fertig“ (kein X, kein Esc, kein Klick daneben).
const SicherDialog = ({ open, onClose, gesperrt, titel, beschreibung, beschreibungSichtbar = true, testid, children }) => (
  <Dialog open={open} onOpenChange={(o) => { if (!o && !gesperrt) onClose(); }}>
    <DialogContent hideCloseButton={gesperrt} className="max-h-[90vh] overflow-y-auto sm:max-w-xl" data-testid={testid}>
      <DialogHeader>
        <DialogTitle className="font-[Manrope]">{titel}</DialogTitle>
        <DialogDescription className={beschreibungSichtbar ? '' : 'sr-only'}>{beschreibung}</DialogDescription>
      </DialogHeader>
      {children}
    </DialogContent>
  </Dialog>
);

const FehlerZeile = ({ text }) => (text ? <p className="text-sm text-red-600" role="alert" data-testid="zwei-faktor-dialog-fehler">{text}</p> : null);

export const EinrichtenDialog = ({ open, onClose }) => {
  const { user, kontoNeuLaden } = useAuth();
  const [codesSichtbar, setCodesSichtbar] = useState(false);
  const [pruefe, setPruefe] = useState(false);
  const schliessen = () => { setCodesSichtbar(false); setPruefe(false); onClose(); };
  const fertig = () => { toast.success(EINGERICHTET); kontoNeuLaden(); schliessen(); };
  return (
    <SicherDialog open={open} onClose={schliessen} gesperrt={codesSichtbar || pruefe} titel="Zwei-Faktor-Anmeldung einrichten"
      beschreibung="Einfach Schritt für Schritt – das dauert etwa 2 Minuten." testid="zwei-faktor-einrichten-dialog">
      {open && <Assistent konto={user?.email} onFertig={fertig} onAbbrechen={schliessen} onCodesSichtbar={setCodesSichtbar} onPruefen={setPruefe} />}
    </SicherDialog>
  );
};

export const NeuesHandyDialog = ({ open, onClose }) => {
  const { user, kontoNeuLaden } = useAuth();
  const [passwort, setPasswort] = useState('');
  const [code, setCode] = useState('');
  const [laedt, setLaedt] = useState(false);
  const [fehler, setFehler] = useState('');
  const [startDaten, setStartDaten] = useState(null);
  const [codesSichtbar, setCodesSichtbar] = useState(false);
  const [pruefe, setPruefe] = useState(false);
  const schliessen = () => { setPasswort(''); setCode(''); setFehler(''); setStartDaten(null); setCodesSichtbar(false); setPruefe(false); onClose(); };
  const weiter = async (e) => {
    e.preventDefault();
    setLaedt(true);
    setFehler('');
    try {
      const res = await api.post('/auth/me/2fa/neu-einrichten', { passwort, code: code.trim() });
      setPasswort('');
      setStartDaten(res.data);
    } catch (err) {
      setFehler(fehlertext(err, ALLGEMEIN));
    } finally {
      setLaedt(false);
    }
  };
  const fertig = () => { toast.success(EINGERICHTET); kontoNeuLaden(); schliessen(); };
  return (
    <SicherDialog open={open} onClose={schliessen} gesperrt={codesSichtbar || pruefe} titel="Neues Handy einrichten"
      beschreibung="Solange das alte Handy noch da ist: Bestätigen Sie mit Ihrem Passwort und dem Code aus der App. Danach richten Sie das neue Handy ein. Der alte Code gilt, bis das neue Handy fertig eingerichtet ist."
      beschreibungSichtbar={!startDaten} testid="zwei-faktor-neues-handy-dialog">
      {startDaten ? (
        <Assistent startDaten={startDaten} konto={user?.email} onFertig={fertig} onAbbrechen={schliessen} onCodesSichtbar={setCodesSichtbar} onPruefen={setPruefe} />
      ) : (
        <form onSubmit={weiter} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="neues-handy-passwort">Passwort</Label>
            <Input id="neues-handy-passwort" type="password" value={passwort} onChange={(e) => setPasswort(e.target.value)} autoComplete="current-password" data-testid="neues-handy-passwort" />
          </div>
          <div className="space-y-2">
            <Label htmlFor="neues-handy-code">Code aus der App oder Wiederherstellungscode</Label>
            <Input id="neues-handy-code" value={code} onChange={(e) => setCode(e.target.value)} autoComplete="one-time-code" data-testid="neues-handy-code" />
          </div>
          <FehlerZeile text={fehler} />
          <DialogFooter className="gap-2">
            <Button type="button" variant="outline" onClick={schliessen} data-testid="neues-handy-abbrechen">Abbrechen</Button>
            <Button type="submit" className="bg-blue-800 hover:bg-blue-900" disabled={laedt || !passwort || !codeGueltig(code)} data-testid="neues-handy-weiter">
              {laedt && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Weiter
            </Button>
          </DialogFooter>
        </form>
      )}
    </SicherDialog>
  );
};

export const NeueCodesDialog = ({ open, onClose }) => {
  const { user, kontoNeuLaden } = useAuth();
  const [code, setCode] = useState('');
  const [laedt, setLaedt] = useState(false);
  const [fehler, setFehler] = useState('');
  const [codes, setCodes] = useState(null);
  const schliessen = () => { setCode(''); setFehler(''); setCodes(null); onClose(); };
  const erzeugen = async (e) => {
    e.preventDefault();
    setLaedt(true);
    setFehler('');
    try {
      const res = await api.post('/auth/me/2fa/wiederherstellungscodes', { code: code.trim() });
      setCodes(res.data.wiederherstellungscodes);
    } catch (err) {
      setFehler(fehlertext(err, ALLGEMEIN));
    } finally {
      setLaedt(false);
    }
  };
  return (
    <SicherDialog open={open} onClose={schliessen} gesperrt={Boolean(codes)} titel="Neue Wiederherstellungscodes"
      beschreibung="Sie bekommen 10 neue Codes. Die bisherigen Codes gelten danach nicht mehr." testid="zwei-faktor-neue-codes-dialog">
      {codes ? (
        <CodesAnzeige codes={codes} konto={user?.email} onFertig={() => { kontoNeuLaden(); schliessen(); }} />
      ) : (
        <form onSubmit={erzeugen} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="neue-codes-code">Code aus der App oder Wiederherstellungscode</Label>
            <Input id="neue-codes-code" value={code} onChange={(e) => setCode(e.target.value)} autoComplete="one-time-code" data-testid="neue-codes-code" />
          </div>
          <FehlerZeile text={fehler} />
          <DialogFooter className="gap-2">
            <Button type="button" variant="outline" onClick={schliessen} data-testid="neue-codes-abbrechen">Abbrechen</Button>
            <Button type="submit" className="bg-blue-800 hover:bg-blue-900" disabled={laedt || !codeGueltig(code)} data-testid="neue-codes-erzeugen">
              {laedt && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Neue Codes erzeugen
            </Button>
          </DialogFooter>
        </form>
      )}
    </SicherDialog>
  );
};

export const SitzungenDialog = ({ open, onClose }) => {
  const { uebernehmeToken } = useAuth();
  const [laedt, setLaedt] = useState(false);
  const beenden = async () => {
    setLaedt(true);
    try {
      const res = await api.post('/auth/me/sitzungen-beenden');
      uebernehmeToken(res.data.access_token);
      toast.success('Alle anderen Sitzungen wurden beendet.');
      onClose();
    } catch {
      toast.error('Die Sitzungen konnten nicht beendet werden.');
    } finally {
      setLaedt(false);
    }
  };
  return (
    <SicherDialog open={open} onClose={onClose} gesperrt={laedt} titel="Alle Sitzungen beenden?"
      beschreibung="Sie werden auf allen anderen Geräten abgemeldet, und alle gemerkten Geräte werden vergessen. Auf diesem Gerät bleiben Sie angemeldet." testid="zwei-faktor-sitzungen-dialog">
      <DialogFooter className="gap-2">
        <Button type="button" variant="outline" onClick={onClose} disabled={laedt} data-testid="sitzungen-abbrechen">Abbrechen</Button>
        <Button type="button" variant="destructive" onClick={beenden} disabled={laedt} data-testid="sitzungen-beenden">
          {laedt ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Wird beendet...</> : 'Sitzungen beenden'}
        </Button>
      </DialogFooter>
    </SicherDialog>
  );
};
